import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { FaArrowLeft, FaCopy, FaCheck } from 'react-icons/fa';

const Container = styled.div`
  padding: 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 16px;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 1rem;

  svg {
    font-size: 20px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  color: var(--text-primary);
  margin: 0 0 1.5rem 0;
`;

const Card = styled.div`
  background-color: var(--bg-secondary);
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
`;

const Description = styled.p`
  color: var(--text-secondary);
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
`;

const CodeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background-color: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 5px;
  padding: 0.75rem;
  margin-bottom: 1.5rem;
`;

const Code = styled.span`
  font-family: monospace;
  font-size: 16px;
  color: var(--text-primary);
  flex: 1;
`;

const CopyButton = styled.button<{ copied: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${props => props.copied ? 'var(--success-color)' : 'var(--primary-color)'};
  color: white;
  border: none;
  border-radius: 5px;
  padding: 0.5rem 1rem;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => props.copied ? 'var(--success-color)' : 'var(--primary-color-dark)'};
  }

  svg {
    font-size: 16px;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
`;

const StatCard = styled.div`
  background-color: var(--bg-primary);
  border-radius: 5px;
  padding: 1rem;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

const Referral: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const referralCode = 'TAXIMOOVE2024';

  const stats = [
    { value: '3', label: t('referral.referrals') },
    { value: '€30', label: t('referral.earned') },
    { value: '2', label: t('referral.pending') }
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Container>
      <BackButton onClick={() => navigate('/profile')}>
        <FaArrowLeft />
        {t('common.back')}
      </BackButton>

      <Title>{t('referral.title')}</Title>

      <Card>
        <SectionTitle>{t('referral.yourCode')}</SectionTitle>
        <Description>{t('referral.description')}</Description>
        
        <CodeContainer>
          <Code>{referralCode}</Code>
          <CopyButton copied={copied} onClick={handleCopy}>
            {copied ? (
              <>
                <FaCheck />
                {t('referral.copied')}
              </>
            ) : (
              <>
                <FaCopy />
                {t('referral.copy')}
              </>
            )}
          </CopyButton>
        </CodeContainer>

        <StatsContainer>
          {stats.map((stat, index) => (
            <StatCard key={index}>
              <StatValue>{stat.value}</StatValue>
              <StatLabel>{stat.label}</StatLabel>
            </StatCard>
          ))}
        </StatsContainer>
      </Card>
    </Container>
  );
};

export default Referral; 